export const SPEND_LINES = Object.freeze([
  { id: 'research', label: 'Research and planning', note: 'Market and competitor research, strategy and Cue’s working time.' },
  { id: 'images', label: 'Images', note: 'Posters, product shots, carousels and stills.' },
  { id: 'video', label: 'Video', note: 'Reels, ads and motion clips.' },
  { id: 'audio', label: 'Voice and music', note: 'Voice-over, music and sound.' },
  { id: 'reserve', label: 'Changes and redos', note: 'Held back for your corrections so they never eat into new work.' },
]);
export const SPEND_TIERS = Object.freeze(['premium', 'balanced', 'draft']);
export const RESEARCH_SKILLS = Object.freeze(['marketing-research', 'marketing-strategist', 'marketing-media-planner', 'marketing-analytics']);
const LINE_IDS = SPEND_LINES.map(line => line.id);
const TIER_MODE = { premium: 'quality', balanced: 'balanced', draft: 'fast' };
const STEP_DOWN = { premium: 'balanced', balanced: 'draft', draft: 'draft' };
const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const text = (value, max = 500) => String(value ?? '').trim().slice(0, max);
const money = value => Math.max(0, Math.round((Number.isFinite(Number(value)) ? Number(value) : 0) * 100) / 100);
const fail = message => { throw new Error(message); };

export function normalizeSpendPlan(value) {
  const plan = object(value);
  const lines = LINE_IDS.map(id => {
    const row = (Array.isArray(plan.lines) ? plan.lines : []).find(item => item?.id === id) || {};
    return { id, amount: money(row.amount), note: text(row.note) };
  });
  const tiers = Object.fromEntries(Object.entries(object(plan.tiers)).filter(([id, tier]) => text(id, 100) && SPEND_TIERS.includes(tier)).slice(0, 1000));
  return {
    revision: Math.max(0, Math.floor(Number(plan.revision) || 0)),
    status: ['proposed', 'agreed'].includes(plan.status) ? plan.status : 'none',
    total: money(plan.total),
    lines,
    tiers,
    reason: text(plan.reason, 2000),
    proposedBy: text(plan.proposedBy, 100),
    proposedAt: text(plan.proposedAt, 40),
    agreedBy: text(plan.agreedBy, 100),
    agreedAt: text(plan.agreedAt, 40),
    agreedRevision: Math.max(0, Math.floor(Number(plan.agreedRevision) || 0)),
  };
}

export function applySpendPlanOp(campaign, op, { actor, at, human }) {
  const current = normalizeSpendPlan(campaign.spendPlan);
  if (op.op === 'spend-plan') {
    const value = object(op.value);
    const next = normalizeSpendPlan({ ...current, lines: value.lines ?? current.lines, tiers: value.tiers ? { ...current.tiers, ...object(value.tiers) } : current.tiers, reason: value.reason ?? current.reason });
    const deliverableIds = new Set((campaign.deliverables || []).map(d => d.id));
    for (const id of Object.keys(next.tiers)) if (!deliverableIds.has(id)) delete next.tiers[id];
    next.total = money(next.lines.reduce((sum, line) => sum + line.amount, 0));
    if (!next.total) fail('Put an amount on at least one line of the budget split.');
    const ceiling = money(campaign.brief?.budget);
    if (ceiling && next.total > ceiling) fail(`The split adds up to ₹${next.total.toLocaleString('en-IN')}, above the ₹${ceiling.toLocaleString('en-IN')} budget.`);
    if (value.agree === true) human();
    const changed = JSON.stringify([next.lines, next.tiers]) !== JSON.stringify([current.lines, current.tiers]);
    if (!changed && current.status !== 'none' && value.agree !== true) { campaign.spendPlan = { ...current, reason: next.reason }; return; }
    const revision = changed || current.status === 'none' ? current.revision + 1 : current.revision;
    campaign.spendPlan = value.agree === true
      ? { ...next, revision, status: 'agreed', proposedBy: changed ? actor : current.proposedBy || actor, proposedAt: changed ? at : current.proposedAt || at, agreedBy: actor, agreedAt: at, agreedRevision: revision }
      : { ...next, revision, status: 'proposed', proposedBy: actor, proposedAt: at, agreedBy: '', agreedAt: '', agreedRevision: current.agreedRevision };
    return;
  }
  if (op.op === 'agree-spend-plan') {
    human();
    if (current.status === 'none') fail('Cue has not proposed a budget split yet.');
    if (Number(op.revision) !== current.revision) fail('The budget split changed. Review its current version.');
    campaign.spendPlan = { ...current, status: 'agreed', agreedBy: actor, agreedAt: at, agreedRevision: current.revision };
  }
}

export function lineForCharge({ operation = '', bucket = '', mode = '', redo = false } = {}) {
  if (LINE_IDS.includes(bucket)) return bucket;
  if (redo) return 'reserve';
  const op = String(operation);
  if (op.startsWith('Agent:')) return 'research';
  if (op === 'Generate footage' || mode === 'video') return 'video';
  if (['audio', 'voice', 'music', 'sfx'].includes(mode)) return 'audio';
  if (op === 'Generate media' || mode === 'image') return 'images';
  return '';
}

export function spendPlanUsage(planInput, budget) {
  const plan = normalizeSpendPlan(planInput);
  const used = Object.fromEntries(LINE_IDS.map(id => [id, 0]));
  for (const row of budget?.reservations || []) {
    if (!['reserved', 'settled'].includes(row.status)) continue;
    const line = lineForCharge({ operation: row.operation, bucket: row.bucket });
    if (line) used[line] += row.status === 'settled' ? Number(row.actualInr) || 0 : Number(row.reservedInr) || 0;
  }
  return SPEND_LINES.map(meta => {
    const amount = plan.lines.find(line => line.id === meta.id)?.amount || 0;
    const spent = money(used[meta.id]);
    return { ...meta, amount, spent, left: money(Math.max(0, amount - spent)), over: spent > amount };
  });
}

const VIDEO_KINDS = new Set(['video']), IMAGE_KINDS = new Set(['image', 'carousel']), AUDIO_KINDS = new Set(['audio']);
const kindsFor = line => line === 'video' ? VIDEO_KINDS : line === 'audio' ? AUDIO_KINDS : IMAGE_KINDS;

function piecesStillToMake(campaign, line) {
  const kinds = kindsFor(line);
  let pieces = 0;
  for (const d of campaign?.deliverables || []) {
    if (!kinds.has(d.kind)) continue;
    const want = Math.max(1, Number(d.assetCount) || 1);
    const have = (campaign.content || []).filter(item => item.deliverableId === d.id).reduce((n, item) => n + (item.assetIds?.length || 0), 0);
    pieces += Math.max(0, want - have);
  }
  return pieces;
}

export function generationAllowance(campaign, budget, { mode = 'image', deliverableId = '', redo = false } = {}) {
  const plan = normalizeSpendPlan(campaign?.spendPlan);
  if (plan.status === 'none') return { status: 'none' };
  const line = lineForCharge({ mode, redo }) || 'images';
  const usage = spendPlanUsage(plan, budget).find(row => row.id === line);
  const baseTier = plan.tiers[deliverableId] || 'balanced';
  const low = usage.amount > 0 && usage.left < usage.amount * 0.25;
  const tier = low ? STEP_DOWN[baseTier] : baseTier;
  const piecesLeft = line === 'reserve' ? 1 : Math.max(1, piecesStillToMake(campaign, line));
  const share = usage.left / piecesLeft;
  const perPieceInr = money(Math.min(usage.left, tier === 'premium' ? share * 2 : tier === 'draft' ? share * 0.6 : share));
  return { status: plan.status, line, label: usage.label, amount: usage.amount, spent: usage.spent, left: usage.left, piecesLeft, tier, baseTier, steppedDown: low && tier !== baseTier, qualityMode: TIER_MODE[tier], perPieceInr };
}

export function spendPlanBlock(campaign, budget, { mode, deliverableId, redo, costInr }) {
  const allowance = generationAllowance(campaign, budget, { mode, deliverableId, redo });
  if (allowance.status === 'none') return '';
  if (allowance.status === 'proposed') return 'The owner has not agreed the budget split yet. Ask them to agree it (or change it) before anything paid runs.';
  const cost = money(costInr);
  if (cost > allowance.left) return `The ${allowance.label} line has ₹${allowance.left.toLocaleString('en-IN')} left of ₹${allowance.amount.toLocaleString('en-IN')}, and this can cost up to ₹${cost.toLocaleString('en-IN')}. Choose a cheaper model, or ask the owner to move money between lines in the budget split.`;
  return '';
}

export function researchBlock(campaign, budget, skillId) {
  if (!RESEARCH_SKILLS.includes(skillId)) return '';
  const plan = normalizeSpendPlan(campaign?.spendPlan);
  if (plan.status !== 'agreed') return '';
  const line = spendPlanUsage(plan, budget).find(row => row.id === 'research');
  if (line.amount > 0 && line.left <= 0) return `The Research and planning line (₹${line.amount.toLocaleString('en-IN')}) is used up. Move money into it in the budget split to run more research.`;
  return '';
}

export function spendPlanView(campaign, budget) {
  const plan = normalizeSpendPlan(campaign?.spendPlan);
  return { ...plan, usage: spendPlanUsage(plan, budget) };
}

export function spendPlanLimitIssue(campaign, budget) {
  const plan = normalizeSpendPlan(campaign?.spendPlan);
  if (plan.status === 'none' || !budget) return '';
  if (plan.total > budget.ceilingInr) return `The split adds up to ₹${plan.total.toLocaleString('en-IN')}, above the ₹${budget.ceilingInr.toLocaleString('en-IN')} production limit. Lower the split or raise the production limit first.`;
  return '';
}
